import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://litbuyspreadsheets.pics';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About litbuyspreadsheets.pics',
  url: `${siteUrl}/about`,
  description: 'Learn about litbuyspreadsheets.pics, an independent educational resource dedicated to helping users understand Litbuy Spreadsheet with guides, tutorials, and product management insights.',
};

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <title>About Litbuy Spreadsheet Guide | litbuyspreadsheets.pics</title>
      <meta name="description" content="Learn about litbuyspreadsheets.pics, an independent educational resource dedicated to helping users understand and maximize their use of Litbuy Spreadsheet with in-depth guides and tutorials." />
      <link rel="canonical" href={`${siteUrl}/about`} />
      <meta property="og:title" content="About Litbuy Spreadsheet Guide | litbuyspreadsheets.pics" />
      <meta property="og:description" content="Learn about litbuyspreadsheets.pics, an independent educational resource for Litbuy Spreadsheet guides and tutorials." />
      <meta property="og:url" content={`${siteUrl}/about`} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="About Litbuy Spreadsheet Guide | litbuyspreadsheets.pics" />
      <meta name="twitter:description" content="Learn about litbuyspreadsheets.pics, an independent educational resource for Litbuy Spreadsheet guides and tutorials." />
      <JsonLd data={aboutSchema} />
      <Navbar />
      <main className="pt-24 pb-16 px-6 md:px-10 lg:px-16">
        <article className="max-w-3xl mx-auto">
          <header className="mb-10">
            <span className="inline-block px-3 py-1 rounded-full border border-accent-200 text-xs font-medium text-accent-600 tracking-wide mb-4">
              About Us
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              About litbuyspreadsheets.pics
            </h1>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
              Your trusted resource for Litbuy Spreadsheet guides, tutorials, and product management insights.
            </p>
          </header>

          <div className="space-y-6 text-sm md:text-base text-foreground-700 leading-relaxed">
            <p>
              Welcome to litbuyspreadsheets.pics, an independent educational resource dedicated to helping users understand and maximize their use of Litbuy Spreadsheet. We are a team of spreadsheet enthusiasts, e-commerce professionals, and data management experts who believe in the power of well-organized product data.
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">Our Mission</h2>
            <p>
              Our mission is simple: provide clear, accurate, and actionable information about Litbuy Spreadsheet. Whether you are a complete beginner learning the basics or an experienced user seeking advanced workflow optimization, our guides are designed to help you succeed. We publish in-depth tutorials, honest comparisons, practical tips, and comprehensive resources — all freely available to anyone who wants to learn.
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">What We Cover</h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-primary-500 text-xs">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                <strong className="text-foreground-900">Beginner Guides</strong> — Step-by-step walkthroughs for new Litbuy Spreadsheet users.
              </li>
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-primary-500 text-xs">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                <strong className="text-foreground-900">Tutorials &amp; Tips</strong> — Advanced techniques to boost your productivity and streamline workflows.
              </li>
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-primary-500 text-xs">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                <strong className="text-foreground-900">Honest Comparisons</strong> — Unbiased evaluations of Litbuy Spreadsheet against Google Sheets, Excel, and other tools.
              </li>
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-primary-500 text-xs">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                <strong className="text-foreground-900">Resources &amp; Templates</strong> — Ready-to-use templates and frameworks for your product data needs.
              </li>
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-primary-500 text-xs">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                <strong className="text-foreground-900">Safety &amp; Security</strong> — Guidance on keeping your data secure while using spreadsheet tools.
              </li>
            </ul>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">Our Independence</h2>
            <p>
              We are an independent resource and are not officially affiliated with Litbuy. Our content is created based on research, hands-on testing, and community feedback. While we strive for accuracy, we always encourage readers to verify information directly through official Litbuy channels before making business decisions.
            </p>

            <div className="mt-8 bg-background-100 rounded-2xl p-6 border border-background-200/60">
              <p className="text-xs text-foreground-500 leading-relaxed">
                <strong className="text-foreground-700">Important:</strong> This website provides educational information about Litbuy Spreadsheet. All product names, logos, and brands are property of their respective owners. We do not store user data, and external links may lead to third-party websites with their own privacy policies. Always verify information and maintain your own data backups.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
              <a href="/" className="text-foreground-600 hover:text-primary-500 transition-colors duration-200 border-b border-transparent hover:border-primary-300 pb-0.5 whitespace-nowrap">
                &larr; Back to Home
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}